import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { useTenant } from '../contexts/TenantContext'
import { useAuth } from '../contexts/AuthContext'
import appointmentFirestoreService from '../services/appointmentFirestoreService'
import CalendarService from '../services/calendarService'

const AppointmentScheduler = ({ isDarkMode }) => {
  const { tenant } = useTenant()
  const { user } = useAuth()
  
  // State management
  const [activeTab, setActiveTab] = useState('calendar') // calendar, settings, bookings
  const [appointments, setAppointments] = useState([])
  const [availability, setAvailability] = useState({
    monday: { enabled: true, start: '09:00', end: '17:00' },
    tuesday: { enabled: true, start: '09:00', end: '17:00' },
    wednesday: { enabled: true, start: '09:00', end: '17:00' },
    thursday: { enabled: true, start: '09:00', end: '17:00' },
    friday: { enabled: true, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '09:00', end: '17:00' },
    sunday: { enabled: false, start: '09:00', end: '17:00' }
  })
  const [meetingTypes, setMeetingTypes] = useState([
    { id: 1, name: 'Quick Call', duration: 15, color: 'bg-blue-500', description: '15-minute intro call' },
    { id: 2, name: 'Demo Call', duration: 30, color: 'bg-purple-500', description: '30-minute product demo' },
    { id: 3, name: 'Strategy Session', duration: 60, color: 'bg-green-500', description: '1-hour strategy discussion' }
  ])
  const [bookingSettings, setBookingSettings] = useState({
    bufferTime: 15, // minutes between meetings
    advanceBooking: 30, // days in advance
    minNotice: 120, // minimum minutes notice
    timezone: 'America/New_York',
    confirmationEmail: true,
    reminderEmail24h: true,
    reminderEmail1h: true,
    requirePhone: false,
    requireCompany: true,
    customQuestions: []
  })
  const [showNewMeetingModal, setShowNewMeetingModal] = useState(false)
  const [showEmbedCodeModal, setShowEmbedCodeModal] = useState(false)
  const [selectedMeetingType, setSelectedMeetingType] = useState(null)
  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [editingMeetingType, setEditingMeetingType] = useState(null)
  const [newMeetingType, setNewMeetingType] = useState({
    name: '',
    duration: 30,
    description: '',
    color: 'bg-blue-500'
  })
  const [newAppointment, setNewAppointment] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    time: '09:00',
    duration: 30,
    notes: '',
    meetingType: 'Demo Call'
  })
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(true)
  const [calendarSyncStats, setCalendarSyncStats] = useState({
    google: 0,
    outlook: 0,
    lastSynced: null
  })

  useEffect(() => {
    const syncConnectionState = () => {
      setGoogleCalendarConnected(localStorage.getItem('google_calendar_connected') === 'true')
    }

    syncConnectionState()
    window.addEventListener('focus', syncConnectionState)

    return () => window.removeEventListener('focus', syncConnectionState)
  }, [])

  const getSortDate = (appointment) => {
    const fallbackTime = appointment.time && !['All day', 'Task'].includes(appointment.time)
      ? appointment.time
      : '00:00'

    return new Date(appointment.startTime || `${appointment.date}T${fallbackTime}`)
  }

  const mergeAppointments = (firestoreAppointments = [], externalAppointments = []) => {
    const merged = [...firestoreAppointments]
    const seen = new Set(
      firestoreAppointments.map((apt) =>
        apt.externalCalendarId || `${apt.startTime || `${apt.date}T${apt.time || '00:00'}`}-${(apt.clientName || apt.meetingType || '').trim().toLowerCase()}`
      )
    )

    externalAppointments.forEach((apt) => {
      const key = apt.externalCalendarId || `${apt.startTime || `${apt.date}T${apt.time || '00:00'}`}-${(apt.clientName || apt.meetingType || '').trim().toLowerCase()}`
      if (!seen.has(key)) {
        seen.add(key)
        merged.push(apt)
      }
    })

    return merged.sort((a, b) => getSortDate(a) - getSortDate(b))
  }

  // Load appointments, availability, meeting types, and synced calendar events
  useEffect(() => {
    const loadAppointmentData = async () => {
      if (!user?.uid || !tenant?.id) {
        setIsLoadingAppointments(false)
        return
      }

      try {
        setIsLoadingAppointments(true)

        const fetchedAppointments = await appointmentFirestoreService.getAppointments(tenant.id)

        // Load availability settings
        const fetchedAvailability = await appointmentFirestoreService.getAvailability(tenant.id)
        if (fetchedAvailability) {
          setAvailability(fetchedAvailability)
        }

        // Load meeting types
        const fetchedMeetingTypes = await appointmentFirestoreService.getMeetingTypes(tenant.id)
        if (fetchedMeetingTypes && fetchedMeetingTypes.length > 0) {
          setMeetingTypes(fetchedMeetingTypes)
        }

        // Load booking settings
        const fetchedBookingSettings = await appointmentFirestoreService.getBookingSettings(tenant.id)
        if (fetchedBookingSettings) {
          setBookingSettings(fetchedBookingSettings)
        }

        // Import external calendar items for the visible time range
        const externalAppointments = []
        const syncStats = {
          google: 0,
          outlook: 0,
          lastSynced: new Date().toISOString()
        }

        const rangeStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
        rangeStart.setMonth(rangeStart.getMonth() - 1)
        const rangeEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0, 23, 59, 59, 999)

        if (localStorage.getItem('google_calendar_connected') === 'true') {
          const googleResult = await CalendarService.getGoogleCalendarItems({
            startDate: rangeStart,
            endDate: rangeEnd
          })

          if (googleResult.success) {
            externalAppointments.push(...googleResult.items)
            syncStats.google = googleResult.items.length
          } else {
            console.warn('Google Calendar sync skipped:', googleResult.error)
          }
        }

        if (localStorage.getItem('microsoft_graph_connected') === 'true') {
          const outlookResult = await CalendarService.getOutlookCalendarItems({
            startDate: rangeStart,
            endDate: rangeEnd
          })

          if (outlookResult.success) {
            externalAppointments.push(...outlookResult.items)
            syncStats.outlook = outlookResult.items.length
          } else {
            console.warn('Outlook Calendar sync skipped:', outlookResult.error)
          }
        }

        setAppointments(mergeAppointments(fetchedAppointments, externalAppointments))
        setCalendarSyncStats(syncStats)
        console.log('✅ Appointment data loaded and calendar sync completed')
      } catch (error) {
        console.error('Error loading appointment data:', error)
        toast.error('Failed to load appointment data')
      } finally {
        setIsLoadingAppointments(false)
      }
    }

    loadAppointmentData()
  }, [user, tenant, currentDate])

  // Save appointments to Firestore
  const saveAppointments = async (updatedAppointments) => {
    setAppointments(updatedAppointments)
  }

  // Handle calendar day click
  const handleDayClick = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    setSelectedDate(dateStr)
    setNewAppointment({
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      time: '09:00',
      duration: 30,
      notes: '',
      meetingType: 'Demo Call'
    })
    setShowAddAppointmentModal(true)
  }

  // Add new appointment
  const handleAddAppointment = async () => {
    if (!newAppointment.clientName || !newAppointment.clientEmail || !newAppointment.time) {
      toast.error('Please fill in all required fields')
      return
    }

    if (!tenant?.id) {
      toast.error('Tenant ID is required')
      return
    }

    try {
      const appointmentData = {
        date: selectedDate,
        clientName: newAppointment.clientName,
        clientEmail: newAppointment.clientEmail,
        clientPhone: newAppointment.clientPhone || '',
        time: newAppointment.time,
        duration: newAppointment.duration,
        notes: newAppointment.notes || '',
        meetingType: newAppointment.meetingType,
        status: 'confirmed',
        startTime: new Date(`${selectedDate}T${newAppointment.time}`).toISOString()
      }

      const savedAppointment = await appointmentFirestoreService.createAppointment(tenant.id, appointmentData)
      
      const updatedAppointments = [...appointments, savedAppointment]
      setAppointments(updatedAppointments)
      
      setShowAddAppointmentModal(false)
      toast.success(`Appointment scheduled for ${selectedDate} at ${newAppointment.time}`)
      
      // Sync with connected calendar if available
      if (googleCalendarConnected || localStorage.getItem('microsoft_graph_connected') === 'true') {
        const calendarEvent = {
          title: `${appointmentData.meetingType} - ${appointmentData.clientName}`,
          description: `Meeting with ${appointmentData.clientName}\nEmail: ${appointmentData.clientEmail}\nPhone: ${appointmentData.clientPhone || 'Not provided'}\nNotes: ${appointmentData.notes || 'No additional notes'}`,
          start: new Date(appointmentData.startTime),
          end: new Date(new Date(appointmentData.startTime).getTime() + appointmentData.duration * 60000),
          attendees: [appointmentData.clientEmail],
          location: 'Online Meeting'
        }

        try {
          const syncUpdates = {}
          const syncedProviders = []
          const syncFailures = []

          if (localStorage.getItem('microsoft_graph_connected') === 'true') {
            const outlookResult = await CalendarService.createOutlookEvent(calendarEvent)
            if (outlookResult.success) {
              syncUpdates.outlookEventId = outlookResult.eventId
              syncedProviders.push('Outlook')
            } else {
              syncFailures.push(`Outlook: ${outlookResult.error}`)
            }
          }

          if (googleCalendarConnected) {
            const googleResult = await CalendarService.createGoogleEvent(calendarEvent)
            if (googleResult.success) {
              syncUpdates.googleEventId = googleResult.eventId
              syncUpdates.externalCalendarId = googleResult.eventId
              syncUpdates.calendarProvider = 'google'
              syncedProviders.push('Google')
            } else {
              syncFailures.push(`Google: ${googleResult.error}`)
            }
          }

          if (Object.keys(syncUpdates).length > 0) {
            await appointmentFirestoreService.updateAppointment(savedAppointment.id, syncUpdates)
            toast.success(`Appointment also added to ${syncedProviders.join(' & ')} Calendar${syncedProviders.length > 1 ? 's' : ''}`)
          }

          if (syncFailures.length > 0) {
            toast.error(`Calendar sync failed: ${syncFailures.join(' | ')}`)
          }
        } catch (calendarError) {
          console.error('Calendar sync error:', calendarError)
          toast.error('Appointment created, but calendar sync failed')
        }
      }
    } catch (error) {
      console.error('Error creating appointment:', error)
      toast.error('Failed to create appointment')
    }
  }

  // Generate booking page URL
  const getBookingPageURL = (meetingType) => {
    const baseURL = window.location.origin
    return `${baseURL}/book/${tenant?.id || 'demo'}/${meetingType?.id || 'default'}`
  }

  // Generate embed code
  const generateEmbedCode = (meetingType) => {
    const bookingURL = getBookingPageURL(meetingType)
    return `<!-- Market Genie Booking Widget -->
<div id="market-genie-booking"></div>
<script>
  (function() {
    var iframe = document.createElement('iframe');
    iframe.src = '${bookingURL}';
    iframe.style.width = '100%';
    iframe.style.height = '600px';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '8px';
    document.getElementById('market-genie-booking').appendChild(iframe);
  })();
</script>`
  }

  // Calendar view component
  const CalendarView = () => {
    const today = new Date()
    
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate()
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay()
    
    const prevMonth = () => {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))
    }
    
    const nextMonth = () => {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))
    }

    const getAppointmentsForDay = (day) => {
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      return appointments.filter(apt => apt.date === dateStr)
    }

    return (
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-5`}>
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            {(googleCalendarConnected || localStorage.getItem('microsoft_graph_connected') === 'true') && (
              <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Synced {calendarSyncStats.google} Google item(s) and {calendarSyncStats.outlook} Outlook item(s)
              </div>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentDate(new Date(currentDate.getTime()))}
              className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              🔄 Sync
            </button>
            <button
              onClick={prevMonth}
              className="px-3 py-1.5 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600"
            >
              ← Prev
            </button>
            <button
              onClick={() => setCurrentDate(today)}
              className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              Today
            </button>
            <button
              onClick={nextMonth}
              className="px-3 py-1.5 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600"
            >
              Next →
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1.5">
          {/* Day headers */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center font-semibold text-gray-600 py-1.5 text-sm">
              {day}
            </div>
          ))}
          
          {/* Empty cells for first week */}
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`empty-${i}`} className="h-20 bg-gray-50 rounded"></div>
          ))}
          
          {/* Day cells */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1
            const dayAppointments = getAppointmentsForDay(day)
            const isToday = today.getDate() === day && 
                           today.getMonth() === currentDate.getMonth() &&
                           today.getFullYear() === currentDate.getFullYear()
            
            return (
              <div
                key={day}
                onClick={() => handleDayClick(day)}
                className={`h-20 border rounded-lg p-1.5 ${
                  isToday ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white'
                } hover:border-teal-400 hover:shadow-md cursor-pointer transition-all`}
              >
                <div className="font-semibold text-gray-900 text-sm">{day}</div>
                <div className="text-xs space-y-0.5 mt-0.5">
                  {dayAppointments.slice(0, 2).map((apt, idx) => (
                    <div
                      key={idx}
                      className="bg-teal-100 text-teal-800 px-2 py-1 rounded truncate"
                      title={`${apt.time} - ${apt.clientName || apt.meetingType}`}
                    >
                      {apt.clientName || apt.meetingType}
                    </div>
                  ))}
                  {dayAppointments.length > 2 && (
                    <div className="text-gray-500 text-center">+{dayAppointments.length - 2} more</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // Settings view component
  const SettingsView = () => (
    <div className="space-y-6">
      {/* Google Calendar Integration Status */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
        <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          📅 Google Calendar Integration
        </h3>
        {googleCalendarConnected ? (
          <div className="space-y-3">
            <div className="flex items-center space-x-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <span className="text-3xl">✅</span>
              <div>
                <div className="font-semibold text-green-900">Connected to Google Calendar</div>
                <div className="text-sm text-green-700">
                  Google Calendar events now sync into this Appointments view and new appointments push back to Google
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                localStorage.removeItem('google_calendar_connected')
                localStorage.removeItem('google_calendar_token')
                localStorage.removeItem('google_calendar_refresh_token')
                localStorage.removeItem('google_calendar_token_expires_at')
                localStorage.removeItem('google_calendar_user_email')
                setGoogleCalendarConnected(false)
                toast.success('Google Calendar disconnected')
              }}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
            >
              Disconnect Google Calendar
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <span className="text-3xl">⚠️</span>
              <div>
                <div className="font-semibold text-yellow-900">Not Connected</div>
                <div className="text-sm text-yellow-700">
                  Connect your Google Calendar to automatically sync appointments
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                toast('Please go to API Keys & Integrations to connect Google Calendar')
                // Navigate to API Keys section
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              🔗 Go to API Keys & Integrations
            </button>
            <p className="text-sm text-gray-600">
              💡 <strong>How to connect:</strong> Navigate to "API Keys & Integrations" section → 
              Find "Google Calendar" → Click "Connect" → Authorize with your Google Workspace account
            </p>
          </div>
        )}
      </div>

      {/* Booking Links & Share Section */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
        <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          🔗 Booking Links & Embed Codes
        </h3>
        <div className="space-y-4">
          <p className="text-gray-600">
            Share these booking links with clients so they can schedule appointments with you directly:
          </p>
          {meetingTypes.map(type => (
            <div key={type.id} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${type.color}`}></div>
                  <div>
                    <div className="font-semibold">{type.name}</div>
                    <div className="text-sm text-gray-600">{type.duration} minutes</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedMeetingType(type)
                    setShowEmbedCodeModal(true)
                  }}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 text-sm"
                >
                  📋 Get Link & Embed Code
                </button>
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={getBookingPageURL(type)}
                  readOnly
                  className="flex-1 text-sm border rounded px-3 py-2 bg-gray-50 text-gray-700"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(getBookingPageURL(type))
                    toast.success(`${type.name} booking link copied!`)
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                >
                  Copy Link
                </button>
              </div>
            </div>
          ))}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">💡</span>
              <div className="text-sm text-blue-900">
                <strong>How clients book appointments:</strong>
                <ol className="list-decimal ml-5 mt-2 space-y-1">
                  <li>You send them the booking link (via email, text, or social media)</li>
                  <li>They click the link and see your available time slots</li>
                  <li>They select a time that works for them</li>
                  <li>They fill in their contact info (name, email, phone)</li>
                  <li>Appointment is instantly confirmed and appears in your calendar</li>
                  {googleCalendarConnected && (
                    <li className="text-green-700 font-medium">
                      ✅ Appointment syncs automatically with your Google Calendar
                    </li>
                  )}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Availability Settings */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
        <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          📅 Weekly Availability
        </h3>
        <div className="space-y-3">
          {Object.entries(availability).map(([day, settings]) => (
            <div key={day} className="flex items-center space-x-4">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => setAvailability({
                  ...availability,
                  [day]: { ...settings, enabled: e.target.checked }
                })}
                className="w-5 h-5"
              />
              <span className="w-24 capitalize font-medium">{day}</span>
              {settings.enabled && (
                <>
                  <input
                    type="time"
                    value={settings.start}
                    onChange={(e) => setAvailability({
                      ...availability,
                      [day]: { ...settings, start: e.target.value }
                    })}
                    className="border rounded px-3 py-2"
                  />
                  <span>to</span>
                  <input
                    type="time"
                    value={settings.end}
                    onChange={(e) => setAvailability({
                      ...availability,
                      [day]: { ...settings, end: e.target.value }
                    })}
                    className="border rounded px-3 py-2"
                  />
                </>
              )}
            </div>
          ))}
        </div>
        <div className="mt-6">
          <button
            onClick={async () => {
              try {
                if (!tenant?.id) {
                  toast.error('Tenant ID required')
                  return
                }
                await appointmentFirestoreService.updateAvailability(tenant.id, availability)
                toast.success('Availability settings saved!')
              } catch (error) {
                console.error('Error saving availability:', error)
                toast.error('Failed to save availability settings')
              }
            }}
            className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
          >
            💾 Save Availability
          </button>
        </div>
      </div>

      {/* Meeting Types */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            🎯 Meeting Types
          </h3>
          <button
            onClick={() => setShowNewMeetingModal(true)}
            className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
          >
            + Add Meeting Type
          </button>
        </div>
        <div className="space-y-3">
          {meetingTypes.map(type => (
            <div key={type.id} className="flex items-center justify-between border rounded-lg p-4">
              <div className="flex items-center space-x-4">
                <div className={`w-4 h-4 rounded-full ${type.color}`}></div>
                <div>
                  <div className="font-semibold">{type.name}</div>
                  <div className="text-sm text-gray-600">{type.duration} minutes - {type.description}</div>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setSelectedMeetingType(type)
                    setShowEmbedCodeModal(true)
                  }}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                >
                  Get Link
                </button>
                <button
                  onClick={() => {
                    setEditingMeetingType(type)
                    setNewMeetingType({
                      name: type.name,
                      duration: type.duration,
                      description: type.description,
                      color: type.color
                    })
                    setShowNewMeetingModal(true)
                  }}
                  className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={async () => {
                    if (window.confirm(`Delete "${type.name}"?`)) {
                      try {
                        await appointmentFirestoreService.deleteMeetingType(type.id)
                        setMeetingTypes(meetingTypes.filter(mt => mt.id !== type.id))
                        toast.success('Meeting type deleted!')
                      } catch (error) {
                        console.error('Error deleting meeting type:', error)
                        toast.error('Failed to delete meeting type')
                      }
                    }
                  }}
                  className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Booking Settings */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
        <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          ⚙️ Booking Settings
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Buffer Time Between Meetings</label>
            <select
              value={bookingSettings.bufferTime}
              onChange={(e) => setBookingSettings({...bookingSettings, bufferTime: parseInt(e.target.value)})}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="0">No buffer</option>
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Minimum Notice</label>
            <select
              value={bookingSettings.minNotice}
              onChange={(e) => setBookingSettings({...bookingSettings, minNotice: parseInt(e.target.value)})}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="60">1 hour</option>
              <option value="120">2 hours</option>
              <option value="240">4 hours</option>
              <option value="1440">1 day</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={bookingSettings.confirmationEmail}
                onChange={(e) => setBookingSettings({...bookingSettings, confirmationEmail: e.target.checked})}
                className="w-4 h-4"
              />
              <span>Send confirmation email</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={bookingSettings.reminderEmail24h}
                onChange={(e) => setBookingSettings({...bookingSettings, reminderEmail24h: e.target.checked})}
                className="w-4 h-4"
              />
              <span>Send reminder 24 hours before</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={bookingSettings.reminderEmail1h}
                onChange={(e) => setBookingSettings({...bookingSettings, reminderEmail1h: e.target.checked})}
                className="w-4 h-4"
              />
              <span>Send reminder 1 hour before</span>
            </label>
          </div>
        </div>
        <div className="mt-6">
          <button
            onClick={async () => {
              try {
                if (!tenant?.id) {
                  toast.error('Tenant ID required')
                  return
                }
                await appointmentFirestoreService.updateBookingSettings(tenant.id, bookingSettings)
                toast.success('Booking settings saved!')
              } catch (error) {
                console.error('Error saving booking settings:', error)
                toast.error('Failed to save booking settings')
              }
            }}
            className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
          >
            💾 Save Booking Settings
          </button>
        </div>
      </div>
    </div>
  )

  // Bookings list view
  const BookingsView = () => {
    const upcomingAppointments = appointments
      .filter(apt => getSortDate(apt) > new Date())
      .sort((a, b) => getSortDate(a) - getSortDate(b))

    const pastAppointments = appointments
      .filter(apt => getSortDate(apt) <= new Date())
      .sort((a, b) => getSortDate(b) - getSortDate(a))

    const AppointmentCard = ({ apt }) => (
      <div className="border rounded-lg p-4 hover:border-teal-500 transition-colors">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-semibold text-lg">{apt.clientName}</div>
            {apt.sourceLabel && (
              <div className="text-xs font-medium text-blue-600">{apt.sourceLabel}</div>
            )}
            <div className="text-sm text-gray-600">{apt.clientEmail}</div>
            {apt.clientPhone && <div className="text-sm text-gray-600">📞 {apt.clientPhone}</div>}
            {apt.clientCompany && <div className="text-sm text-gray-600">🏢 {apt.clientCompany}</div>}
          </div>
          <div className="text-right">
            <div className="font-semibold">{apt.meetingType}</div>
            <div className="text-sm text-gray-600">📅 {new Date(`${apt.date}T00:00:00`).toLocaleDateString()}</div>
            <div className="text-sm text-gray-600">🕐 {apt.time}</div>
          </div>
        </div>
        {apt.notes && (
          <div className="mt-3 text-sm bg-gray-50 p-2 rounded">
            <strong>Notes:</strong> {apt.notes}
          </div>
        )}
        {apt.readOnly ? (
          <div className="mt-3 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
            Synced from your external calendar. Edit this item in Google Calendar or Outlook.
          </div>
        ) : (
          <div className="mt-3 flex space-x-2">
            <button className="px-3 py-1 bg-teal-500 text-white rounded text-sm hover:bg-teal-600">
              Join Meeting
            </button>
            <button className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600">
              Reschedule
            </button>
            <button className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600">
              Cancel
            </button>
          </div>
        )}
      </div>
    )

    return (
      <div className="space-y-6">
        {/* Upcoming Appointments */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
          <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            📅 Upcoming Appointments ({upcomingAppointments.length})
          </h3>
          {upcomingAppointments.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No upcoming appointments scheduled
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingAppointments.map((apt, idx) => (
                <AppointmentCard key={idx} apt={apt} />
              ))}
            </div>
          )}
        </div>

        {/* Past Appointments */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
          <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            📁 Past Appointments ({pastAppointments.length})
          </h3>
          {pastAppointments.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No past appointments
            </div>
          ) : (
            <div className="space-y-3">
              {pastAppointments.slice(0, 10).map((apt, idx) => (
                <AppointmentCard key={idx} apt={apt} />
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Add Appointment Modal
  const renderAddAppointmentModal = () => {
    if (!showAddAppointmentModal) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                📅 Schedule New Appointment
              </h2>
              <button
                onClick={() => setShowAddAppointmentModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Date Display */}
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <div className="text-sm text-teal-600 font-medium">Selected Date</div>
                <div className="text-lg font-bold text-teal-900">
                  {selectedDate && new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>

              {/* Client Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newAppointment.clientName}
                  onChange={(e) => setNewAppointment({ ...newAppointment, clientName: e.target.value })}
                  placeholder="John Smith"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Client Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={newAppointment.clientEmail}
                  onChange={(e) => setNewAppointment({ ...newAppointment, clientEmail: e.target.value })}
                  placeholder="john@company.com"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Client Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Phone (Optional)
                </label>
                <input
                  type="tel"
                  value={newAppointment.clientPhone}
                  onChange={(e) => setNewAppointment({ ...newAppointment, clientPhone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Time and Duration Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={newAppointment.time}
                    onChange={(e) => setNewAppointment({ ...newAppointment, time: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (minutes)
                  </label>
                  <select
                    value={newAppointment.duration}
                    onChange={(e) => setNewAppointment({ ...newAppointment, duration: parseInt(e.target.value) })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">60 minutes</option>
                    <option value="90">90 minutes</option>
                  </select>
                </div>
              </div>

              {/* Meeting Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meeting Type
                </label>
                <select
                  value={newAppointment.meetingType}
                  onChange={(e) => setNewAppointment({ ...newAppointment, meetingType: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  {meetingTypes.map(type => (
                    <option key={type.id} value={type.name}>{type.name}</option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={newAppointment.notes}
                  onChange={(e) => setNewAppointment({ ...newAppointment, notes: e.target.value })}
                  placeholder="Any additional notes or agenda items..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Google Calendar Sync Notice */}
              {googleCalendarConnected && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center space-x-2">
                  <span className="text-2xl">📅</span>
                  <div className="text-sm text-blue-700">
                    This appointment will be automatically synced with your Google Calendar
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleAddAppointment}
                className="flex-1 px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 font-semibold transition-colors"
              >
                📅 Schedule Appointment
              </button>
              <button
                onClick={() => setShowAddAppointmentModal(false)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Embed code modal
  const EmbedCodeModal = () => {
    if (!showEmbedCodeModal || !selectedMeetingType) return null

    const bookingURL = getBookingPageURL(selectedMeetingType)
    const embedCode = generateEmbedCode(selectedMeetingType)

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Share Your Booking Page
              </h2>
              <button
                onClick={() => {
                  setShowEmbedCodeModal(false)
                  setSelectedMeetingType(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Direct Link */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📎 Direct Booking Link
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={bookingURL}
                  readOnly
                  className="flex-1 border rounded-lg px-3 py-2 bg-gray-50"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(bookingURL)
                    toast.success('Link copied to clipboard!')
                  }}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
                >
                  Copy
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Share this link in emails, social media, or anywhere else
              </p>
            </div>

            {/* Embed Code */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🔗 Embed Code (for your website)
              </label>
              <textarea
                value={embedCode}
                readOnly
                rows={8}
                className="w-full border rounded-lg px-3 py-2 bg-gray-50 font-mono text-sm"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(embedCode)
                  toast.success('Embed code copied!')
                }}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Copy Embed Code
              </button>
              <p className="text-sm text-gray-600 mt-2">
                Paste this code into your website HTML to embed the booking widget
              </p>
            </div>

            {/* QR Code Placeholder */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📱 QR Code
              </label>
              <div className="border rounded-lg p-8 bg-gray-50 text-center">
                <div className="text-6xl mb-2">📱</div>
                <p className="text-gray-600">QR Code generation coming soon!</p>
                <p className="text-sm text-gray-500 mt-1">
                  Scan to book appointments on mobile
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`min-h-screen p-6 ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-teal-50 to-blue-100'}`}>
      {/* Header */}
      <div className="mb-6">
        <h1 className={`text-2xl font-bold mb-1.5 ${isDarkMode ? 'text-teal-400' : 'text-teal-900'}`}>
          📅 Appointment Scheduler
        </h1>
        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Manage your calendar and let clients book time with you
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-4`}>
          <div className="text-2xl mb-1.5">📅</div>
          <div className="text-lg font-bold">{appointments.filter(apt => getSortDate(apt) > new Date()).length}</div>
          <div className="text-gray-600 text-xs">Upcoming</div>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-4`}>
          <div className="text-2xl mb-1.5">✅</div>
          <div className="text-lg font-bold">{appointments.filter(apt => getSortDate(apt) <= new Date()).length}</div>
          <div className="text-gray-600 text-xs">Completed</div>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-4`}>
          <div className="text-2xl mb-1.5">🎯</div>
          <div className="text-lg font-bold">{meetingTypes.length}</div>
          <div className="text-gray-600 text-xs">Meeting Types</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-2 mb-4">
        <button
          onClick={() => setActiveTab('calendar')}
          className={`px-4 py-2 rounded-lg font-semibold transition-colors text-sm ${
            activeTab === 'calendar'
              ? 'bg-teal-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          📅 Calendar
        </button>
        <button
          onClick={() => setActiveTab('bookings')}
          className={`px-4 py-2 rounded-lg font-semibold transition-colors text-sm ${
            activeTab === 'bookings'
              ? 'bg-teal-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          📋 Bookings
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 rounded-lg font-semibold transition-colors text-sm ${
            activeTab === 'settings'
              ? 'bg-teal-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          ⚙️ Settings
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'calendar' && <CalendarView />}
      {activeTab === 'bookings' && <BookingsView />}
      {activeTab === 'settings' && <SettingsView />}

      {/* Modals */}
      {renderAddAppointmentModal()}
      <AddMeetingTypeModal />
      <EmbedCodeModal />
    </div>
  )

  // Add Meeting Type Modal
  function AddMeetingTypeModal() {
    if (!showNewMeetingModal) return null

    const handleSaveMeetingType = async () => {
      if (!newMeetingType.name || !newMeetingType.duration) {
        toast.error('Please fill in all required fields')
        return
      }

      try {
        if (editingMeetingType) {
          // Update existing
          const updated = await appointmentFirestoreService.saveMeetingType(tenant.id, {
            ...newMeetingType,
            id: editingMeetingType.id
          })
          setMeetingTypes(meetingTypes.map(mt => mt.id === editingMeetingType.id ? updated : mt))
          toast.success('Meeting type updated!')
        } else {
          // Create new
          const saved = await appointmentFirestoreService.saveMeetingType(tenant.id, newMeetingType)
          setMeetingTypes([...meetingTypes, saved])
          toast.success('Meeting type created!')
        }
        setShowNewMeetingModal(false)
        setEditingMeetingType(null)
        setNewMeetingType({ name: '', duration: 30, description: '', color: 'bg-blue-500' })
      } catch (error) {
        console.error('Error saving meeting type:', error)
        toast.error('Failed to save meeting type')
      }
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingMeetingType ? '✏️ Edit Meeting Type' : '➕ Add Meeting Type'}
              </h2>
              <button
                onClick={() => {
                  setShowNewMeetingModal(false)
                  setEditingMeetingType(null)
                  setNewMeetingType({ name: '', duration: 30, description: '', color: 'bg-blue-500' })
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meeting Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newMeetingType.name}
                  onChange={(e) => setNewMeetingType({ ...newMeetingType, name: e.target.value })}
                  placeholder="30-Minute Consultation"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration <span className="text-red-500">*</span>
                </label>
                <select
                  value={newMeetingType.duration}
                  onChange={(e) => setNewMeetingType({ ...newMeetingType, duration: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="90">90 minutes</option>
                  <option value="120">2 hours</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newMeetingType.description}
                  onChange={(e) => setNewMeetingType({ ...newMeetingType, description: e.target.value })}
                  placeholder="Brief description of what this meeting is for..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <div className="flex space-x-2">
                  {['bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 'bg-pink-500'].map(color => (
                    <button
                      key={color}
                      onClick={() => setNewMeetingType({ ...newMeetingType, color })}
                      className={`w-10 h-10 rounded-full ${color} ${newMeetingType.color === color ? 'ring-4 ring-gray-400' : ''}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleSaveMeetingType}
                className="flex-1 px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 font-semibold transition-colors"
              >
                {editingMeetingType ? '💾 Update' : '➕ Create'}
              </button>
              <button
                onClick={() => {
                  setShowNewMeetingModal(false)
                  setEditingMeetingType(null)
                  setNewMeetingType({ name: '', duration: 30, description: '', color: 'bg-blue-500' })
                }}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default AppointmentScheduler
